import React from 'react';
import PropTypes from 'prop-types';
import { BiPlus, BiPlusCircle, BiUpArrowCircle, BiX } from 'react-icons/bi';
import { Flex } from 'rebass';
import {
  JournalCard,
  CardTitle,
  SpecRow,
  SpecText,
  DetailsLink,
  EntitlementChip,
  RemoveButton,
  PrimaryButton,
  OutlineButton,
  AddOnsLink,
} from '../styled';
import { entitlementLabel, specLine } from '../entitlements';
import Chevron from '../chevron';

// Multiple journals can share a first name — the API has no child/parent
// relationship to group or distinguish them by otherwise (see
// docs/child_journal_editions.md §4) — so repeats are suffixed - 2, - 3, …,
// the same convention already used for repeat gifts (see gifts/gift-card.js).
export const bookTitle = (journal, suffix) =>
  suffix > 1 ? `${journal.firstName} - ${suffix}` : journal.firstName;

// A journal the customer owns. No pencil/edit icon beside the name — that was
// removed from this version of the design.
//
// Assigned add-on chips are deliberately not listed here: once a book is in My
// Books its add-ons live behind "Details" and in the spec line. Only *staged*
// add-ons show, so the customer can see what Confirm is about to apply.
//
// While an add-on is selected in Credits the primary action becomes "Add to this
// book", exactly as on the Unassigned tiles — it picks this journal as the target
// and leaves the actual allocation to Confirm.
const BookCard = ({
  journal,
  titleSuffix,
  hasSelection,
  pendingChips,
  disabled,
  onDetails,
  onStart,
  onAddToBook,
  onRemovePending,
  onPurchase,
  onUpgradeBundle,
  isAddOnsMode
}) => {
  const spec = specLine(journal.entitlements);
  const label = journal.state === 'initialized' ? 'View book' : 'Start my book';

  return (
    <JournalCard>
      <CardTitle>{bookTitle(journal, titleSuffix)}</CardTitle>

      <SpecRow>
        <SpecText>{spec || 'Baby book'}</SpecText>
        <DetailsLink type="button" onClick={() => onDetails(journal)}>
          Details
        </DetailsLink>
      </SpecRow>

      {pendingChips.map(chip => (
        <EntitlementChip key={chip.id} pending>
          {entitlementLabel(chip)}
          <RemoveButton type="button" onClick={() => onRemovePending(chip.id)}>
            Remove
            <BiX size={20} />
          </RemoveButton>
        </EntitlementChip>
      ))}

      <div >
        {(hasSelection || pendingChips.length > 0 || isAddOnsMode) ? (
          <OutlineButton type="button" disabled={!hasSelection} onClick={() => onAddToBook(journal.id)}>
            <BiPlus size={20} />
            Add to this book
          </OutlineButton>
        ) : (
          <PrimaryButton type="button" onClick={() => onStart(journal)}>
            {label}
            <Chevron />
          </PrimaryButton>
        )}
      </div>
      <Flex py={1}>
        {journal.entitlements?.['plan-name'] !== 'Platinum Bundle' && <AddOnsLink type="button" onClick={() => onUpgradeBundle(journal)}>
          <BiUpArrowCircle size={18} />
          Upgrade Bundle
        </AddOnsLink>}
        {journal.entitlements?.['plan-name'] !== 'Platinum Bundle' && <Flex width="1px" bg="#D1D1D1" height="44px" />}

        <AddOnsLink type="button" onClick={() => onPurchase(journal)}>
          <BiPlusCircle size={18} />
          Add-ons
        </AddOnsLink>
      </Flex>
    </JournalCard>
  );
};

BookCard.propTypes = {
  journal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    entitlements: PropTypes.object,
    state: PropTypes.string,
    storyCount: PropTypes.number,
  }).isRequired,
  titleSuffix: PropTypes.number,
  hasSelection: PropTypes.bool,
  pendingChips: PropTypes.array,
  disabled: PropTypes.bool,
  onDetails: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  onAddToBook: PropTypes.func,
  onRemovePending: PropTypes.func,
  onPurchase: PropTypes.func,
  onUpgradeBundle: PropTypes.func,
};

BookCard.defaultProps = {
  titleSuffix: 1,
  hasSelection: false,
  pendingChips: [],
  disabled: false,
  onAddToBook: () => { },
  onRemovePending: () => { },
  onPurchase: () => { },
  onUpgradeBundle: () => { },
};

export default BookCard;
